import { getCurrentCountry, getTaskEntryDoor } from "@config";
import {
  getBackendVisitorId,
  trackEvent,
} from "@Libs/GrowthBook/services/growthbook";
import {
  ITrackingParams,
  pushDataLayerEvent,
} from "@Libs/Tracking/services/dataLayer";

import { IAnswerMap, IAnswerValue, IServiceRequestDraft } from "../types";

import { P_CATEGORY_NAMES_FR } from "./constants";

/**
 * GA4 event names for the new flow, verbatim from HUB-5279. "NF" is the new
 * flow, and the prefix is what separates these from the legacy flow's
 * `Task Published` / `Task Subcategory Selected`, which keep firing from
 * `app/assets/javascripts/classes/task_creation_form.js.coffee`.
 */
export const TRACKING_EVENTS = {
  DESCRIPTION_SUBMITTED: "NF Task Main Description Submitted",
  TYPE_OF_SERVICE_SELECTED: "NF Type Of Service Selected",
  DETAILS_ADDED: "NF Task Details Added",
  AVAILABILITIES_SELECTED: "NF Task Availabilities Selected",
  ADDRESS_ENTERED: "NF Task Address Entered",
  PHOTOS_ADDED: "NF Task Photos Added",
  AREA_ENTERED: "NF Task Area Entered",
  VISIBILITY_SELECTED: "NF Task Visibility Selected",
  TASK_PUBLISHED: "NF Task Published",
  /** GrowthBook only — see `trackNoCategoryFit`. */
  NO_CATEGORY_FIT: "NF Task No Category Fit",
  /** Pre-existing GA4/Google Ads event — kept verbatim, unlike "NF ..." above. */
  FIRST_SERVICE_REQUEST_CREATED: "First Service Request Created",
} as const;

/**
 * `step_name` for the description screen.
 *
 * It is the flow's root route and the only screen with no config key of its own
 * (`TaskCreationFlow` reads the key from the URL, and there is none here), but
 * it is step 1 of the funnel — leaving `step_name` empty would drop the largest
 * step from every GA4 Explore report built on that dimension.
 */
export const DESCRIPTION_STEP_NAME = "description";

/**
 * Which screen submission fires which event.
 *
 * Keyed, never positional: screens come from the per-category backend config,
 * so their order and count differ per trade — `area` reaches only painter and
 * `photos` skips bicycle_repairer. A screen with no entry here fires nothing,
 * so a category-specific screen a later config adds stays out of the funnel
 * until someone names its event.
 *
 * The three `*_type` keys are the "Type of service" screen from the ticket's
 * screenshot — the category-specific question screen. Still unmapped: the
 * `subcategory` picker, which the ticket scopes out, and the `auth` screens,
 * which are steps in the flow rather than answers about the request.
 */
export const SCREEN_EVENTS: Record<string, string> = {
  task_type: TRACKING_EVENTS.TYPE_OF_SERVICE_SELECTED,
  surface_type: TRACKING_EVENTS.TYPE_OF_SERVICE_SELECTED,
  appliance_type: TRACKING_EVENTS.TYPE_OF_SERVICE_SELECTED,
  extra_details: TRACKING_EVENTS.DETAILS_ADDED,
  time: TRACKING_EVENTS.AVAILABILITIES_SELECTED,
  address: TRACKING_EVENTS.ADDRESS_ENTERED,
  photos: TRACKING_EVENTS.PHOTOS_ADDED,
  area: TRACKING_EVENTS.AREA_ENTERED,
  visibility: TRACKING_EVENTS.VISIBILITY_SELECTED,
};

export interface IBuildTrackingParams {
  draft?: IServiceRequestDraft;
  /** The screen being submitted; absent on the description screen, which has none. */
  screenKey?: string;
  isLoggedIn: boolean;
  isEditing?: boolean;
  /**
   * The theme the flow is running under, for a draft that has none yet: it takes
   * its theme from its subcategory, so the description screen's draft of a
   * main-page entry carries no category. The draft's own theme wins when it has one.
   */
  pCategoryId?: number;
}

/**
 * The properties every new-flow event carries.
 *
 * Ids are stringified to match the Ruby builders, which all send
 * `&.id&.to_s` (`GoogleTagManager::Events::TaskDetailsViewed`) — GA4 would
 * otherwise register the same dimension as a number here and a string there.
 *
 * Category names are the French ones the serializer adds for this, never the page
 * locale's, which would split each dimension by language.
 *
 * TODO(HUB-5279, backend): the existing events also send `task_subcategory_2`,
 * `task_subcategory_2_concat`, `task_province`, `task_city`, `task_postal_code`
 * and `task_is_first`, which the draft payload does not carry.
 */
export const buildTrackingParams = ({
  draft,
  screenKey,
  isLoggedIn,
  isEditing = false,
  pCategoryId,
}: IBuildTrackingParams) => {
  const themeId = draft?.p_category_id ?? pCategoryId;

  return {
    flow_type: isEditing ? "edit" : "task_creation",
    flow_version: "new",
    entry_door: getTaskEntryDoor(),
    task_type: draft?.is_private ? "private" : "public",
    step_name: screenKey,
    is_logged_in: isLoggedIn,
    // The legacy flow's name for the same fact; `is_logged_in` stays for existing GTM config.
    user_is_logged_in: isLoggedIn,
    task_category:
      draft?.p_category_name_fr ??
      (themeId === undefined ? undefined : P_CATEGORY_NAMES_FR[themeId]),
    task_category_id: themeId?.toString(),
    task_subcategory_1: draft?.s_category_name_fr,
    task_subcategory_1_id: draft?.s_category_id?.toString(),
    // Lowercase, as `current_country.iso_code_2` is on the Ruby side.
    task_country_code: getCurrentCountry(),
  };
};

/**
 * Whether a submitted value counts as an answer.
 *
 * `0` and `false` are answers, so this is not `Boolean(value)`. `[]` is the
 * photos screen with nothing uploaded and `""` the details screen with nothing
 * typed — both reach here as a value, not as an absent key.
 */
const isAnswered = (value: IAnswerValue | undefined): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && value !== "";

export interface IPushScreenEventParams extends IBuildTrackingParams {
  /**
   * The values the screen just submitted, keyed by question key.
   *
   * `photos` and `extra_details` are `required: false` in every category
   * config, so Continue on an empty form is a successful save of nothing.
   * Without these the "Added" events fired on a skip, reporting photos and
   * details that were never given (HUB-5279).
   */
  values: IAnswerMap;
}

/**
 * One funnel event to both GA4 (the GTM data layer) and GrowthBook (HUB-5597).
 * GrowthBook gets the backend visitor id — the UUID Rails buckets `task_creation`
 * on and sends with the Rails-rendered funnel events — not the SDK's numeric user id.
 */
export const trackFunnelEvent = (
  event: string,
  params?: ITrackingParams
): void => {
  pushDataLayerEvent(event, params);
  trackEvent(event, params, getBackendVisitorId());
};

/**
 * Fire the event this screen maps to, if it maps one and the user answered it.
 *
 * Every config screen's save goes through `DynamicScreen`, including the last
 * one, whose PUT carries `validate` and so *is* the publish.
 *
 * One answered question is enough: a screen asking two optional questions has
 * still been answered when only one is filled, and on a required screen the
 * check can never fail — the form would not have submitted.
 */
export const pushScreenEvent = ({
  values,
  ...params
}: IPushScreenEventParams): void => {
  const event = params.screenKey && SCREEN_EVENTS[params.screenKey];

  if (!event || !Object.values(values).some(isAnswered)) return;

  trackFunnelEvent(event, buildTrackingParams(params));
};

export interface ITrackNoCategoryFitParams extends IBuildTrackingParams {
  /** The submitted text the backend could not match to a category. */
  description: string;
  /** The subcategory the entry door pre-selected, for an entry with no draft yet. */
  sCategoryId?: number;
}

/**
 * The new flow handing a visitor to the legacy picker because no category fits
 * their description (HUB-5609). GrowthBook only: it is a secondary metric, not
 * worth the GTM setup a GA4 event needs.
 */
export const trackNoCategoryFit = ({
  description,
  sCategoryId,
  ...params
}: ITrackNoCategoryFitParams): void => {
  const shared = buildTrackingParams(params);

  trackEvent(
    TRACKING_EVENTS.NO_CATEGORY_FIT,
    {
      ...shared,
      task_subcategory_1_id:
        sCategoryId?.toString() ?? shared.task_subcategory_1_id,
      task_main_description: description,
      task_main_description_length: description.length,
    },
    getBackendVisitorId()
  );
};
